/**
 * The three internationally reserved emergency transponder codes (ICAO Annex 10).
 *
 * Derived from the SQUAWK, deliberately, and not from the ADS-B `emergency` field:
 * the backend declares that field but never assigns it, so it arrives as the literal
 * string "none" on every aircraft. `squawk` is populated and is what controllers and
 * every other tracker key off.
 *
 * `label` is what a glance must resolve to, so it says what is happening rather than
 * repeating the number the badge already shows.
 */
export const EMERGENCY_SQUAWKS = {
  7500: { label: 'HIJACK', description: 'Unlawful interference' },
  7600: { label: 'RADIO FAIL', description: 'Lost communications' },
  7700: { label: 'EMERGENCY', description: 'General emergency' },
};

/**
 * The emergency an aircraft is squawking, or null.
 *
 * Accepts the squawk however it arrives. Transponder codes are four OCTAL digits and
 * the leading zero is significant ("0100"), so a numeric 7700 and the string "7700"
 * must agree while "77000" must not match — hence the exact 4-character test rather
 * than a prefix or a loose == on the number.
 *
 * @param {object|string|number} source aircraft row, or a squawk on its own
 * @returns {{code: string, label: string, description: string}|null}
 */
export function getEmergencySquawk(source) {
  if (source == null) return null;

  const raw = (typeof source === 'object') ? source.squawk : source;
  if (raw == null) return null;

  const code = String(raw).trim();
  // Four digits exactly. A shorter numeric squawk (7700 is never short, but 100 is)
  // is padded, because JSON numbers lose the leading zeros a squawk depends on.
  const padded = code.length < 4 && /^\d+$/.test(code) ? code.padStart(4, '0') : code;
  if (!/^[0-7]{4}$/.test(padded)) return null;

  const match = EMERGENCY_SQUAWKS[padded];
  return match ? { code: padded, ...match } : null;
}

/**
 * True when the aircraft is squawking 7500, 7600 or 7700.
 * @param {object|string|number} source
 * @returns {boolean}
 */
export function isEmergencySquawk(source) {
  return getEmergencySquawk(source) !== null;
}

export default getEmergencySquawk;
